/**
 * Wallet-migration operations: pure selectors behind the Stuck migrations page and the migration
 * detail's Redrive / Abandon / Reactivate commands.
 *
 * Mirrors elios-backend wallet-service 0.72.0 (`nl/migration-hardening-phase1`, PR #176):
 * `wallet-migration/stall.ts` (thresholds), `orchestrator/stale-sweep.service.ts` (who a stalled
 * row waits on, the 24 h automatic-action horizon), `redrive.service.ts` / `abandon.service.ts`
 * (which states each command accepts) and `docs/wallet-reactivation.md` (the one topology the
 * reactivation reconciles). The backend re-checks everything; these only decide what to offer.
 */
import type { MantineColor } from "@mantine/core";
import type { WalletDoc, WalletMigrationDoc, WithDocId } from "@/firestore/types";

export const ACTIVE_MIGRATION_STATES = [
  "requested",
  "provisioningNew",
  "drainingPositions",
  "snapshotting",
  "installingTransferKeys",
  "transferringFunds",
  "transferStalled",
  "rekeyingEntities",
] as const;

export const TERMINAL_MIGRATION_STATES = ["completed", "cleanedUp", "failed"] as const;

const ACTIVE: ReadonlySet<string> = new Set(ACTIVE_MIGRATION_STATES);

export function isActiveMigration(m: Pick<WalletMigrationDoc, "state">): boolean {
  return ACTIVE.has(m.state);
}

const MINUTE_MS = 60_000;

/** `MIGRATION_STALL_AFTER_MS`: how long a row may sit unchanged in each state. */
export const MIGRATION_STALL_AFTER_MS: Readonly<Record<string, number>> = {
  requested: 15 * MINUTE_MS,
  provisioningNew: 75 * MINUTE_MS,
  drainingPositions: 60 * MINUTE_MS,
  snapshotting: 15 * MINUTE_MS,
  installingTransferKeys: 15 * MINUTE_MS,
  transferringFunds: 60 * MINUTE_MS,
  transferStalled: 60 * MINUTE_MS,
  rekeyingEntities: 15 * MINUTE_MS,
};

/** A `completed` row whose +24 h cleanup was never scheduled. */
export const CLEANUP_NOT_SCHEDULED_STALL_AFTER_MS = 15 * MINUTE_MS;

/** The stale sweep only re-drives / reconciles rows changed within this; older ones are only paged. */
export const AUTOMATIC_ACTION_MAX_AGE_MS = 24 * 60 * MINUTE_MS;

type MigrationLike = Pick<WalletMigrationDoc, "state" | "updatedAt"> &
  Partial<
    Pick<
      WalletMigrationDoc,
      | "scheduledCleanupAt"
      | "stalledAfter"
      | "mainTransferSessionKey"
      | "reserveTransferSessionKey"
      | "mainTransferSao"
      | "reserveTransferSao"
      | "stallReason"
      | "waitingOn"
      | "automaticActionsUntil"
      | "ownerRotation"
      | "newWallet"
      | "oldWallet"
      | "id"
    >
  >;

function ms(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export interface StallStatus {
  /** ISO instant past which the row counts as stalled; null when its state never stalls. */
  stalledAfter: string | null;
  /** True when the console computed `stalledAfter` (document predates wallet-service 0.72.0). */
  estimated: boolean;
  stalled: boolean;
  /** How long past `stalledAfter`, or null when not stalled. */
  overdueMs: number | null;
}

/**
 * The projected `stalledAfter`, else the backend's own rule recomputed from `updatedAt`. A limUSD
 * drain may legitimately take days: neither the projection nor this knows the source holds limUSD.
 */
export function stallStatus(m: MigrationLike, now: number): StallStatus {
  let stalledAfter: string | null;
  let estimated = false;
  if (m.stalledAfter !== undefined) {
    stalledAfter = m.stalledAfter;
  } else {
    estimated = true;
    const base = ms(m.updatedAt);
    const threshold =
      m.state === "completed"
        ? m.scheduledCleanupAt
          ? undefined
          : CLEANUP_NOT_SCHEDULED_STALL_AFTER_MS
        : MIGRATION_STALL_AFTER_MS[m.state];
    stalledAfter =
      base === null || threshold === undefined ? null : new Date(base + threshold).toISOString();
  }
  const at = ms(stalledAfter);
  const stalled = at !== null && now > at;
  return { stalledAfter, estimated, stalled, overdueMs: stalled ? now - at! : null };
}

export type WaitingOn = "owner" | "backend";

/**
 * Who has to act for the row to move, as the stale sweep reports it: the projected `waitingOn`
 * (wallet-service 0.75.0), else estimated from the row (in `installingTransferKeys`, the owner
 * once a transfer key is minted, the backend before).
 */
export function waitingOn(m: MigrationLike): WaitingOn | null {
  if (m.waitingOn !== undefined)
    return m.waitingOn === "owner" || m.waitingOn === "backend" ? m.waitingOn : null;
  switch (m.state) {
    case "provisioningNew":
    case "transferStalled":
      return "owner";
    case "installingTransferKeys":
      return m.mainTransferSessionKey || m.reserveTransferSessionKey ? "owner" : "backend";
    case "completed":
      return m.scheduledCleanupAt ? null : "backend";
    case "cleanedUp":
    case "failed":
      return null;
    default:
      return ACTIVE.has(m.state) ? "backend" : null;
  }
}

/** True when the console estimated the stall verdict (the document predates 0.75.0). */
export function stallVerdictEstimated(m: MigrationLike): boolean {
  return m.waitingOn === undefined;
}

/** The stale sweep's `stallReason` codes, as the projection and the sweep's pages name them. */
export const STALL_REASON_LABELS: Readonly<Record<string, string>> = {
  requestedNotProgressing: "Provision step not progressing",
  deployNotLanding: "Successor deploy not landing (owner prompts)",
  deployLegsDead: "Successor deploy prompts dead",
  drainOverdue: "Earn drain overdue",
  snapshottingNotProgressing: "Snapshot step not progressing",
  // The sweep's refinements of a quiesce waiting in `snapshotting` (0.77.0, P2-06).
  cardSettlementInFlight: "Card settlement in flight (the snapshot's quiesce waits)",
  quarantinedOperation: "A quarantined operation holds the snapshot's quiesce",
  transferKeysNotMinted: "Transfer keys not minted",
  transferKeysAwaitingOwner: "Transfer keys await the owner's approval",
  // The card-cohort gate (wallet-service 0.77.0, D5): the transfer keys are enabled, the
  // successor's card keys are not. A superAdmin may waive it once per row.
  cardKeysAwaitingOwner: "Successor card keys await the owner's approval",
  transferKeysExpired: "Transfer keys expired",
  transferNotDispatched: "Transfer not dispatched",
  transferLegInFlight: "Transfer legs dispatched",
  awaitingOwnerRetry: "Awaiting the owner's retry",
  rekeyingEntitiesNotProgressing: "Rekey step not progressing",
  cleanupNotScheduled: "Cleanup not scheduled",
};

/** The projected `stallReason` with its label, or null (none projected, or the state never stalls). */
export function stallReason(m: MigrationLike): { code: string; label: string } | null {
  if (!m.stallReason) return null;
  return { code: m.stallReason, label: STALL_REASON_LABELS[m.stallReason] ?? m.stallReason };
}

/**
 * Whether the stale sweep still acts on the row by itself: until the projected
 * `automaticActionsUntil` (0.75.0), else estimated as 24 h after `updatedAt`.
 */
export function automaticActionsActive(m: MigrationLike, now: number): boolean {
  if (m.automaticActionsUntil !== undefined) {
    const until = ms(m.automaticActionsUntil);
    return until !== null && now <= until;
  }
  const updated = ms(m.updatedAt);
  return updated !== null && now - updated <= AUTOMATIC_ACTION_MAX_AGE_MS;
}

// ───────────────────────────── operator commands ─────────────────────────────

export interface CommandAvailability {
  offered: boolean;
  /** What the command will do here, or why it is not offered. */
  note: string;
}

/**
 * A console-side reading of one of the backend's alert markers (a `migrationAlert`,
 * `cardSettlementAlert` or `residualForward` log field that pages), derived from Firestore data.
 */
export interface AlertMarker {
  /** The backend marker it mirrors, e.g. `forwardFailed`, `reserveShortfallAtSnapshot`. */
  kind: string;
  label: string;
  color: MantineColor;
  /** What it means and what to do. */
  detail: string;
}

/** Transfer-leg operation states in which the leg may already be on chain. */
const LEG_IN_FLIGHT = new Set(["submitting", "submitted", "submissionUnknown"]);

/**
 * `POST /walletMigrations/{id}/redrive`: which states it acts on. `transferLegStates` are the
 * states of the row's transfer-leg operations (`mainTransferSao` / `reserveTransferSao`), when
 * read: the backend refuses a `transferringFunds` redrive while a leg may be on chain.
 */
export function redriveAvailability(
  m: MigrationLike,
  transferLegStates: readonly (string | null | undefined)[] = [],
): CommandAvailability {
  switch (m.state) {
    case "requested":
      return {
        offered: true,
        note: "Re-runs the provision step; it adopts a successor an earlier attempt created without stamping it.",
      };
    case "snapshotting":
    case "installingTransferKeys":
    case "rekeyingEntities":
      return {
        offered: true,
        note: "Re-emits the step's progress event so the orchestrator re-runs it.",
      };
    case "provisioningNew":
      return {
        offered: true,
        note: "Re-requests the successor's deploy, re-arming dead deploy prompts.",
      };
    case "drainingPositions":
      return {
        offered: true,
        note: "Re-emits the drain request to earn. An existing drain row is not restarted.",
      };
    case "transferringFunds": {
      const inFlight = transferLegStates.find((state) => !!state && LEG_IN_FLIGHT.has(state));
      return inFlight
        ? {
            offered: false,
            note: `A transfer leg is ${inFlight}: it may already be on chain. Wait for its receipt, or resolve a quarantined (submissionUnknown) leg from its smart-account operation.`,
          }
        : {
            offered: true,
            note: "Re-runs the transfer step; its claim protocol never dispatches a leg twice. Refused (409) if a leg is dispatched meanwhile.",
          };
    }
    case "completed":
      return m.scheduledCleanupAt
        ? { offered: false, note: "Completed with its cleanup scheduled: nothing to redrive." }
        : { offered: true, note: "Re-runs the +24 h cleanup scheduling." };
    case "transferStalled":
      return {
        offered: false,
        note: "Waits for the owner to approve the transfer again. For an owner who cannot, abandon.",
      };
    case "cleanedUp":
    case "failed":
      return { offered: false, note: `A ${m.state} migration cannot be redriven.` };
    default:
      return { offered: false, note: `Unknown state ${m.state}.` };
  }
}

const ABANDON_PRE_TRANSFER = new Set([
  "requested",
  "provisioningNew",
  "drainingPositions",
  "snapshotting",
  "installingTransferKeys",
]);

/**
 * `POST /walletMigrations/{id}/abandon`: ends a migration none of whose transfers executed and
 * gives the owner the source wallet back. A `failed` row qualifies only while its source is still
 * `migrating` (and it is that source's newest migration, which the backend checks).
 */
export function abandonAvailability(
  m: MigrationLike,
  source: Pick<WalletDoc, "state"> | null | undefined,
): CommandAvailability {
  const effect =
    "Restores the source wallet and retires or closes the successor; nothing moves on chain.";
  if (ABANDON_PRE_TRANSFER.has(m.state)) return { offered: true, note: effect };
  if (m.state === "transferringFunds")
    return {
      offered: true,
      note: `${effect} Refused (409) until every stamped transfer key has lapsed.`,
    };
  if (m.state === "transferStalled")
    return { offered: true, note: `${effect} For an owner who cannot or does not retry.` };
  if (m.state === "failed") {
    if (!source) return { offered: false, note: "Loading the source wallet…" };
    return source.state === "migrating"
      ? { offered: true, note: `${effect} The source is still migrating after this failure.` }
      : {
          offered: false,
          note: `The source wallet is ${source.state}, not migrating: nothing to abandon.`,
        };
  }
  return { offered: false, note: `A ${m.state} migration cannot be abandoned.` };
}

/** The successor's own onward rotation (B → C) that a reactivation reconciles. */
export function onwardRotation(
  successorMigrations: readonly Pick<WalletMigrationDoc, "state" | "newWallet" | "createdAt">[],
): Pick<WalletMigrationDoc, "state" | "newWallet" | "createdAt"> | null {
  const cleaned = successorMigrations
    .filter((s) => s.state === "cleanedUp" && !!s.newWallet)
    .sort((a, b) => (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0));
  return cleaned[0] ?? null;
}

export type ReactivationCandidate =
  | {
      offered: true;
      sourceWallet: string;
      sourceUpdatedAt: string;
      currentWallet: string;
      currentWalletUpdatedAt: string;
      note: string;
    }
  | { offered: false; note: string };

/** Whether a migration row has the shape a reactivation reconciles at all. */
export function isReactivationShape(m: MigrationLike): boolean {
  return m.state === "installingTransferKeys" && !!m.ownerRotation && !!m.newWallet;
}

/**
 * The wallet a reactivation would retire (C): the source's projected `currentWallet` (0.75.0, the
 * successor that stands for it now), else inferred from the successor's newest cleaned-up onward
 * rotation on documents that predate the field. `note` explains a C that cannot be reconciled.
 */
export function reactivationCurrentWallet(input: {
  migration: MigrationLike;
  source: Pick<WithDocId<WalletDoc>, "_docId" | "currentWallet">;
  successorMigrations: readonly Pick<WalletMigrationDoc, "state" | "newWallet" | "createdAt">[];
}): { id: string | null; inferred: boolean; note?: string } {
  const { migration, source } = input;
  if (source.currentWallet === undefined) {
    const id = onwardRotation(input.successorMigrations)?.newWallet ?? null;
    return id
      ? { id, inferred: true }
      : {
          id: null,
          inferred: true,
          note: "The successor has no cleaned-up onward rotation (B → C): this is not the reactivation topology. Abandon may apply instead.",
        };
  }
  const id = source.currentWallet;
  if (!id) return { id: null, inferred: false, note: "The owner has no selectable wallet." };
  if (id === source._docId)
    return { id: null, inferred: false, note: "The source is already the owner's current wallet." };
  if (id === migration.newWallet)
    return {
      id: null,
      inferred: false,
      note: "The successor (B) is the owner's current wallet: there is no onward rotation to reconcile. Abandon may apply instead.",
    };
  return { id, inferred: false };
}

/**
 * `POST /wallets/{A}/reactivate`, for the one topology `docs/wallet-reactivation.md` approves: A is
 * `migrating`; its owner rotation A → B stopped at `installingTransferKeys` (expired grants, no
 * transfer submitted); B migrated on to C, the owner's current `provisioned` wallet. Abandon
 * refuses this shape (its successor B already migrated on). Null when the row is not even that
 * kind of rotation, so the console doesn't offer the command at all. The backend re-checks the
 * whole topology (and needs superAdmin in production).
 */
export function reactivationCandidate(input: {
  migration: MigrationLike & Pick<WalletMigrationDoc, "id">;
  source: WithDocId<WalletDoc> | null | undefined;
  successorMigrations: readonly Pick<WalletMigrationDoc, "state" | "newWallet" | "createdAt">[];
  current: WithDocId<WalletDoc> | null | undefined;
}): ReactivationCandidate | null {
  const { migration, source, current } = input;
  if (!isReactivationShape(migration)) return null;
  if (!source) return { offered: false, note: "Loading the source wallet…" };
  if (source.state !== "migrating")
    return { offered: false, note: `The source wallet is ${source.state}, not migrating.` };
  const target = reactivationCurrentWallet({
    migration,
    source,
    successorMigrations: input.successorMigrations,
  });
  if (!target.id) return { offered: false, note: target.note ?? "No current wallet to retire." };
  if (!current || current._docId !== target.id)
    return { offered: false, note: "Loading the current wallet (C)…" };
  if (current.state !== "provisioned")
    return { offered: false, note: `The current wallet (C) is ${current.state}, not provisioned.` };
  return {
    offered: true,
    sourceWallet: source._docId,
    sourceUpdatedAt: source.updatedAt,
    currentWallet: current._docId,
    currentWalletUpdatedAt: current.updatedAt,
    note: "Restores this migration's source as the current wallet, keeps B migrated and retires C. Moves no funds.",
  };
}

// ───────────────────────────── stuck migrations ─────────────────────────────

export type StuckReason =
  | "failedSourceMigrating"
  | "migratingWithoutRow"
  | "stalled"
  | "cleanupNotScheduled"
  | "inFlight";

export const STUCK_REASON_LABELS: Record<StuckReason, string> = {
  failedSourceMigrating: "Failed, source still migrating",
  migratingWithoutRow: "Migrating wallet, no live migration",
  stalled: "Stalled",
  cleanupNotScheduled: "Cleanup never scheduled",
  inFlight: "In flight",
};

const REASON_PRIORITY: Record<StuckReason, number> = {
  failedSourceMigrating: 0,
  migratingWithoutRow: 1,
  stalled: 2,
  cleanupNotScheduled: 3,
  inFlight: 4,
};

export interface StuckRow {
  key: string;
  reason: StuckReason;
  migration: WithDocId<WalletMigrationDoc> | null;
  /** The migration's source, or the migrating wallet itself. */
  sourceWalletId: string;
  sourceState: string | null;
  owner: string | null;
  stall: StallStatus | null;
  /** The projected stall reason (0.75.0), or null. */
  stallReason: { code: string; label: string } | null;
  waitingOn: WaitingOn | null;
  automaticActions: boolean;
  sortAt: number;
}

function newestFirst<T extends Pick<WalletMigrationDoc, "createdAt">>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0));
}

/**
 * Everything an operator may have to unwedge, most actionable first:
 *   1. a `failed` migration that is its source's newest while the source is still `migrating`
 *      (abandon's `failed` case);
 *   2. a `migrating` wallet whose newest migration is neither active nor failed (the sweep's
 *      `migratingWithoutRow` page);
 *   3. active rows past `stalledAfter`, oldest first;
 *   4. `completed` rows whose cleanup was never scheduled;
 *   5. every other active row, oldest first.
 */
export function selectStuckMigrations(input: {
  active: readonly WithDocId<WalletMigrationDoc>[];
  cleanupUnscheduled: readonly WithDocId<WalletMigrationDoc>[];
  migratingWallets: readonly WithDocId<WalletDoc>[];
  /** Every migration whose `oldWallet` is one of `migratingWallets`. */
  migrationsOfMigratingWallets: readonly WithDocId<WalletMigrationDoc>[];
  /** Source wallets by id, for the state column (optional). */
  sourceWallets?: ReadonlyMap<string, Pick<WalletDoc, "state">>;
  now: number;
}): StuckRow[] {
  const { now } = input;
  const rows: StuckRow[] = [];
  const seen = new Set<string>();

  const fromMigration = (
    m: WithDocId<WalletMigrationDoc>,
    reason: StuckReason | null,
    sourceState: string | null,
  ) => {
    if (seen.has(m._docId)) return;
    seen.add(m._docId);
    const stall = stallStatus(m, now);
    const resolved: StuckReason =
      reason ??
      (m.state === "completed" ? "cleanupNotScheduled" : stall.stalled ? "stalled" : "inFlight");
    rows.push({
      key: `migration:${m._docId}`,
      reason: resolved,
      migration: m,
      sourceWalletId: m.oldWallet,
      sourceState: sourceState ?? input.sourceWallets?.get(m.oldWallet)?.state ?? null,
      owner: m.owner ?? null,
      stall: resolved === "failedSourceMigrating" ? null : stall,
      stallReason: resolved === "failedSourceMigrating" ? null : stallReason(m),
      waitingOn: waitingOn(m),
      automaticActions: isActiveMigration(m) && automaticActionsActive(m, now),
      sortAt: ms(m.updatedAt) ?? 0,
    });
  };

  const bySource = new Map<string, WithDocId<WalletMigrationDoc>[]>();
  for (const m of input.migrationsOfMigratingWallets) {
    const list = bySource.get(m.oldWallet) ?? [];
    list.push(m);
    bySource.set(m.oldWallet, list);
  }
  for (const wallet of input.migratingWallets) {
    if (wallet.state !== "migrating") continue;
    const newest = newestFirst(bySource.get(wallet._docId) ?? [])[0];
    if (newest && isActiveMigration(newest)) continue; // listed with the active rows
    if (newest?.state === "failed") {
      fromMigration(newest, "failedSourceMigrating", wallet.state);
      continue;
    }
    rows.push({
      key: `wallet:${wallet._docId}`,
      reason: "migratingWithoutRow",
      migration: newest ?? null,
      sourceWalletId: wallet._docId,
      sourceState: wallet.state,
      owner: wallet.owner ?? null,
      stall: null,
      stallReason: null,
      waitingOn: null,
      automaticActions: false,
      sortAt: ms(wallet.updatedAt) ?? 0,
    });
  }

  for (const m of input.active) if (isActiveMigration(m)) fromMigration(m, null, null);
  for (const m of input.cleanupUnscheduled)
    if (m.state === "completed" && !m.scheduledCleanupAt) fromMigration(m, null, null);

  return rows.sort(
    (a, b) => REASON_PRIORITY[a.reason] - REASON_PRIORITY[b.reason] || a.sortAt - b.sortAt,
  );
}

/** Counts per reason, for the page's summary chips. */
export function countByReason(rows: readonly StuckRow[]): Record<StuckReason, number> {
  const counts: Record<StuckReason, number> = {
    failedSourceMigrating: 0,
    migratingWithoutRow: 0,
    stalled: 0,
    cleanupNotScheduled: 0,
    inFlight: 0,
  };
  for (const row of rows) counts[row.reason] += 1;
  return counts;
}

/** How a migration relates to the wallet being viewed (Wallet 360's Migrations panel). */
export function migrationRelation(
  m: Pick<WalletMigrationDoc, "oldWallet" | "newWallet">,
  walletId: string,
): "from" | "to" | "other" {
  if (m.oldWallet === walletId) return "from";
  if (m.newWallet === walletId) return "to";
  return "other";
}
